import {useAddNotification, useTransition} from '@logistics-frontend/blocks'
import type {ServerError} from '@logistics-frontend/core/utils/network'
import {useState} from 'react'
import {useParams} from 'react-router-dom'

import {fetchEvents, fetchSharedRouteCancel, fetchSharedRouteInfo} from 'src/api/cargo-c2c'
import type * as t from 'src/api/cargo-c2c/types'

import {useSharedRoute} from '../../hooks'
import type {RouterParams} from '../../types'
import {type Message, toCancelMessage} from './utils'

function isCancelPaidError(body: unknown): body is t.CancelPaidError {
  return Boolean(body && typeof body === 'object' && 'message' in body && 'revision' in body)
}

export function useSharedRouteCancel(action: t.ActionCancelV2) {
  const key = useParams<RouterParams>().sharingKey
  const [message, setMessage] = useState<Message | undefined>(undefined)
  const addNotification = useAddNotification()

  const [, startRouteTransition] = useSharedRoute()
  const [{isPending: isEventsPending}] = useTransition(
    () => fetchEvents({key, revision: 0}).then(events => setMessage(toCancelMessage(action, events.revision || 0))),
    {key: 'shared-route/events'},
  )
  const [{isPending: isCancelPending}, startTransition] = useTransition<void, ServerError>(undefined, {
    key: 'shared-route/cancel',
  })

  function cancelHandler(type: t.CancelType, revision: number) {
    startTransition(() => fetchSharedRouteCancel({key, type, revision}), {
      onCommit() {
        startRouteTransition(() => fetchSharedRouteInfo({key}))
      },
      onError(error) {
        if (isCancelPaidError(error.body)) {
          const paidMessage = error.body.message
          const nextMessage: Message = {
            revision: error.body.revision,
            opened: true,
            title: paidMessage.title,
            body: paidMessage.body,
            buttons: [
              {
                type: 'do_not_cancel',
                title: paidMessage.close_button.title,
                variant: 'action',
              },
            ],
          }

          if (paidMessage.confirm_button) {
            nextMessage.buttons.push({
              type: 'paid_cancel',
              title: paidMessage.confirm_button.title,
              variant: 'default',
            })
          }

          return setMessage(nextMessage)
        }

        addNotification(error)
      },
    })
  }

  return {
    isPending: isEventsPending || isCancelPending,
    message,
    cancelHandler,
    openHandler() {
      message && setMessage({...message, opened: true})
    },
    closeHandler() {
      message && setMessage({...message, opened: false})
    },
  } as const
}
